import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { captureClientException } from '@/lib/posthog';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  isStaleAssetError,
  isStaleAssetReloadPending,
  reloadOnceForStaleAsset,
} from '@/lib/deployVersionSync';

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  reloading: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      reloading: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // A stale-asset reload is already committed (e.g. from vite:preloadError). Stay
    // quiet so the hard reload replaces this page instead of the dead-end fallback.
    if (isStaleAssetError(error) && isStaleAssetReloadPending()) {
      return { reloading: true };
    }
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Stale deploy assets: hard-reload once instead of showing a dead-end error UI.
    // If a reload is committed (here or earlier via vite:preloadError), stay quiet and
    // skip reporting — the recovery reload is already in flight.
    if (isStaleAssetError(error)) {
      reloadOnceForStaleAsset('error-boundary');
      if (isStaleAssetReloadPending()) {
        this.setState({ reloading: true });
        return;
      }
    }

    Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
    captureClientException(error, { component_stack: errorInfo?.componentStack });
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    // A stale-asset reload is in flight — render nothing until the page reloads.
    if (this.state.reloading) {
      return null;
    }

    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided (supports function to receive error)
      if (this.props.fallback) {
        const fb = this.props.fallback;
        return typeof fb === 'function' ? fb(this.state.error, this.state.errorInfo!) : fb;
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <div className="flex items-center gap-2 text-destructive mb-2">
                <AlertTriangle className="w-5 h-5" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
              <CardDescription>An error occurred while processing your request.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium">{this.state.error?.message || 'Unknown error'}</p>
                    {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                      <details className="mt-2 text-xs">
                        <summary className="cursor-pointer font-medium">Stack trace</summary>
                        <pre className="mt-2 p-2 bg-muted rounded overflow-auto text-xs">
                          {this.state.error?.stack}
                          {'\n\n'}
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </details>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Button onClick={this.handleReset} variant="outline" className="flex-1">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
                <Button onClick={this.handleReload} variant="default" className="flex-1">
                  <Home className="w-4 h-4 mr-2" />
                  Reload Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
